import * as React from "react";
import { AppShell, DEFAULT_BRAIN_WIDTH } from "@aisquare/ui";
import { Section, VariantBlock } from "../lib/Section";

/**
 * AppShell is `fixed inset-0`, so a raw mount would escape the playground and
 * cover the whole page. Every block wraps it in a `relative` framed window
 * (the AuthShell-demo trick, `className="!absolute"` on the shell) so the
 * full-viewport frame is captured in-place for a screenshot.
 *
 * The unified layout tokens (`--bg`, `--surface`, `--rail-w`, ...) are not in
 * the lib token sheet yet; the component's `var(name, fallback)` chain paints
 * from the playground's shadcn tokens so the frame reads here pre-#43.
 */
const FRAME = "relative h-[440px] w-full overflow-hidden rounded-xl border border-border";

/* -------------------------------------------------------------------------- */
/* Labeled placeholder slot content so the frame reads in a screenshot.        */
/* -------------------------------------------------------------------------- */

function Rail() {
  return (
    <div className="flex h-full flex-col items-center gap-3 py-3 text-[11px] font-semibold text-muted-foreground">
      <div className="grid size-9 place-items-center rounded-[10px] bg-[var(--forest,hsl(var(--accent)))] text-[13px] font-bold text-[hsl(var(--accent-foreground))]">
        AS
      </div>
      <div className="my-1 h-px w-7 bg-border" />
      {["H", "M", "O"].map((t) => (
        <div key={t} className="grid size-9 place-items-center rounded-[10px] border border-border">
          {t}
        </div>
      ))}
      <div className="mt-auto grid size-9 place-items-center rounded-full border border-border">U</div>
    </div>
  );
}

function Center({ label }: { label: string }) {
  return (
    <div className="flex h-full flex-col p-6">
      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        {label} · flex-1 min-w-0
      </div>
      <h2 className="mt-2 text-2xl font-semibold tracking-tight text-foreground">
        What do you want to create?
      </h2>
      <p className="mt-2 max-w-md text-sm text-muted-foreground">
        The elastic pane. It shrinks below its intrinsic width when the Brain
        widens, so the resize never jams.
      </p>
      <div className="mt-auto rounded-[10px] border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
        Composer / routed surface
      </div>
    </div>
  );
}

function Brain({ width }: { width: number }) {
  return (
    <div className="flex h-full flex-col gap-3 p-5 pl-6">
      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        Brain · {Math.round(width)}px
      </div>
      <div className="text-sm font-medium text-foreground">Session artifacts</div>
      {["Experience", "Source", "Draft"].map((t) => (
        <div key={t} className="rounded-[10px] border border-border bg-muted/40 px-3 py-2 text-sm text-foreground">
          {t}
        </div>
      ))}
      <div className="mt-auto text-[11px] text-muted-foreground">
        Drag the left edge, or focus it and press Arrow / Home / End.
      </div>
    </div>
  );
}

/** Controlled Brain wrapper so the splitter + auto-widen buttons move. */
function Shell({
  initialWidth = DEFAULT_BRAIN_WIDTH,
  noBrain = false,
  withTopbar = false,
  centerLabel = "Center",
  showWidenControls = false,
}: {
  initialWidth?: number;
  noBrain?: boolean;
  withTopbar?: boolean;
  centerLabel?: string;
  showWidenControls?: boolean;
}) {
  const [w, setW] = React.useState(initialWidth);
  // Auto-widen policy lives in the consumer (grow-never-shrink + collapse-to-396).
  const widen = (target: number) => setW((cur) => Math.max(cur, target));
  return (
    <div className="w-full">
      {showWidenControls ? (
        <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
          <span className="text-muted-foreground">Auto-widen:</span>
          <button type="button" className="rounded-md border border-border px-2 py-1 hover:bg-accent hover:text-accent-foreground" onClick={() => widen(470)}>
            experience 470
          </button>
          <button type="button" className="rounded-md border border-border px-2 py-1 hover:bg-accent hover:text-accent-foreground" onClick={() => widen(520)}>
            result 520
          </button>
          <button type="button" className="rounded-md border border-border px-2 py-1 hover:bg-accent hover:text-accent-foreground" onClick={() => widen(580)}>
            video 580
          </button>
          <button type="button" className="rounded-md border border-border px-2 py-1 hover:bg-accent hover:text-accent-foreground" onClick={() => setW(396)}>
            collapse 396
          </button>
        </div>
      ) : null}
      <div className={FRAME}>
        <AppShell
          className="!absolute"
          rail={<Rail />}
          topbar={
            withTopbar ? (
              <div className="flex h-full items-center gap-3 px-4 text-sm font-medium text-foreground">
                <div className="grid size-6 place-items-center rounded-md bg-[var(--forest,hsl(var(--accent)))] text-[11px] font-bold text-[hsl(var(--accent-foreground))]">
                  A
                </div>
                AISquare Studio
                <span className="ml-auto rounded-full border border-border px-2.5 py-1 text-xs text-muted-foreground">
                  Personal workspace
                </span>
              </div>
            ) : undefined
          }
          brain={noBrain ? null : <Brain width={w} />}
          brainWidth={w}
          onBrainResize={noBrain ? undefined : setW}
        >
          <Center label={centerLabel} />
        </AppShell>
      </div>
    </div>
  );
}

export function AppShellDemo() {
  return (
    <Section
      id="app-shell"
      title="AppShell"
      description="The experience-OS frame: a FLEX row (never grid on the resizable axis) of rail / center / Brain region. The Brain width is a controlled prop that animates via flex-basis/width; the accessible splitter on its left edge resizes by pointer drag and keyboard (Arrow / Home / End). brain={null} renders a single-column frame that earns its width with a transform-only entrance. Authored against the unified token names (--bg / --surface / --rail-w / ...) with lib-token fallbacks until #43 lands."
    >
      <VariantBlock label="Single-column" caption="brain={null} - a fresh session before it earns its width (transform-only entrance, no splitter)">
        <Shell noBrain centerLabel="Center (single-column)" />
      </VariantBlock>

      <VariantBlock label="3-pane (Brain 396)" caption="default list view; drag the Brain's left edge or focus the splitter and use the keyboard">
        <Shell initialWidth={396} showWidenControls />
      </VariantBlock>

      <VariantBlock label="Focus auto-widen (580)" caption="a video/podcast drill-in opened the Brain wide; widen only ever grows past the manual width">
        <Shell initialWidth={580} />
      </VariantBlock>

      <VariantBlock label="With topbar" caption="optional topbar slot - the same primitive frames the topbar + rail listing surfaces">
        <Shell withTopbar initialWidth={420} />
      </VariantBlock>
    </Section>
  );
}
